/**
 * GraphQL queries and hooks — useAllPages pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { type DocumentNode } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import { useEffect } from 'react';
import type { PageInfo } from '../types/common';

/**
 * Fetch every page of a Relay-style connection.
 *
 * Runs the initial query with `first: batchSize`, then walks the cursor with
 * `fetchMore` until `pageInfo.hasNextPage` is false, so callers receive the
 * full result set instead of a single capped page. Requires a matching
 * `keyArgs: ['filter']` + merge field policy in the Apollo cache (see
 * `mergePaginatedConnection` in lib/apolloClient.ts) so the fetched pages
 * accumulate rather than replace each other.
 *
 * `batchSize` doubles as the initial page size. It is intentionally kept at the
 * caller's `first` default so per-page requests stay within each resolver's
 * server-side `first` cap; the loop removes the artificial list limit without
 * a schema change.
 */
export function useAllPages<TData>(
  query: DocumentNode,
  selectConnection: (data: TData | undefined) => { pageInfo: PageInfo } | null | undefined,
  variables: Record<string, unknown>,
  batchSize: number
): QueryResult<TData> {
  const result = useQuery<TData>(query, {
    variables: { ...variables, first: batchSize },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const connection = selectConnection(result.data);
  const hasNextPage = connection?.pageInfo?.hasNextPage ?? false;
  const endCursor = connection?.pageInfo?.endCursor ?? null;
  const { fetchMore } = result;

  useEffect(() => {
    if (hasNextPage && endCursor) {
      // `fetchMore` merges these variables over the initial ones, so `filter`
      // and `first` are preserved and only the cursor advances.
      void fetchMore({ variables: { after: endCursor } });
    }
  }, [hasNextPage, endCursor, fetchMore]);

  return result;
}
